import { useEffect, useState } from 'react';
import { Package, Users, DollarSign, TrendingUp, Mail, Settings, FileText, CheckCircle, CreditCard, Truck, Tag, FolderOpen, BarChart3, ShoppingCart, Megaphone, Banknote } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import OrdersManagement from '@/components/admin/OrdersManagement';
import ProductsManagement from '@/components/admin/ProductsManagement';
import PaysafeManagement from '@/components/admin/PaysafeManagement';
import EmailLogs from '@/components/admin/EmailLogs';
import EmailTemplates from '@/components/admin/EmailTemplates';
import SystemConfig from '@/components/admin/SystemConfig';
import GatewaySettings from '@/components/admin/GatewaySettings';
import CustomerManagement from '@/components/admin/CustomerManagement';
import TrackingManagement from '@/components/admin/TrackingManagement';
import CouponManagement from '@/components/admin/CouponManagement';
import CollectionsManagement from '@/components/admin/CollectionsManagement';
import AffiliatesManagement from '@/components/admin/AffiliatesManagement';
import PayoutsManagement from '@/components/admin/PayoutsManagement';
import AnalyticsManagement from '@/components/admin/AnalyticsManagement';
import CheckoutSettingsManagement from '@/components/admin/CheckoutSettingsManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold mb-2" data-testid="admin-dashboard-title">Admin Command Center</h1>
          <p className="text-gray-600">Manage your e-commerce store with automated logistics</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid grid-cols-6 md:grid-cols-12 gap-2" data-testid="admin-tabs">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Orders</span>
            </TabsTrigger>
            <TabsTrigger value="tracking" className="flex items-center gap-2">
              <Truck className="h-4 w-4" />
              <span className="hidden sm:inline">Tracking</span>
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Customers</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Products</span>
            </TabsTrigger>
            <TabsTrigger value="coupons" className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              <span className="hidden sm:inline">Coupons</span>
            </TabsTrigger>
            <TabsTrigger value="collections" className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Collections</span>
            </TabsTrigger>
            <TabsTrigger value="gateways" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Gateways</span>
            </TabsTrigger>
            <TabsTrigger value="checkout" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              <span className="hidden sm:inline">Checkout</span>
            </TabsTrigger>
            <TabsTrigger value="paysafe" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Paysafe</span>
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Emails</span>
            </TabsTrigger>
            <TabsTrigger value="affiliates" className="flex items-center gap-2" data-testid="admin-tab-affiliates">
              <Megaphone className="h-4 w-4" />
              <span className="hidden sm:inline">Affiliates</span>
            </TabsTrigger>
            <TabsTrigger value="payouts" className="flex items-center gap-2" data-testid="admin-tab-payouts">
              <Banknote className="h-4 w-4" />
              <span className="hidden sm:inline">Payouts</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="affiliates"><AffiliatesManagement /></TabsContent>
          <TabsContent value="payouts"><PayoutsManagement /></TabsContent>

          {/* Dashboard Overview */}
          <TabsContent value="dashboard" data-testid="dashboard-section">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600">Total Orders</CardTitle>
                      <Package className="h-5 w-5 text-blue-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold" data-testid="stat-total-orders">{stats?.total_orders || 0}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold" data-testid="stat-total-revenue">${stats?.total_revenue?.toFixed(2) || '0.00'}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600">Pending Paysafe</CardTitle>
                      <Users className="h-5 w-5 text-orange-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold" data-testid="stat-pending-paysafe">{stats?.pending_paysafe_verifications || 0}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600">Shipped (24h)</CardTitle>
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold" data-testid="stat-shipped-24h">{stats?.shipped_24h || 0}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Orders by Status */}
                <Card>
                  <CardHeader>
                    <CardTitle>Orders by Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {stats?.orders_by_status && Object.entries(stats.orders_by_status).map(([status, count]) => (
                        <div key={status} className="p-4 bg-gray-50 rounded">
                          <div className="text-2xl font-bold">{count}</div>
                          <div className="text-sm text-gray-600 capitalize">{status}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle>7-Day Activity</CardTitle>
                    <CardDescription>Orders and revenue trends</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stats?.recent_activity && stats.recent_activity.length > 0 ? (
                      <div className="space-y-2">
                        {stats.recent_activity.map((day, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium">{new Date(day.date).toLocaleDateString()}</span>
                            <div className="flex gap-4">
                              <span className="text-sm text-gray-600">{day.count} orders</span>
                              <span className="text-sm font-bold">${parseFloat(day.revenue || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No recent activity</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Orders Management */}
          <TabsContent value="orders">
            <OrdersManagement />
          </TabsContent>

          {/* Analytics Management */}
          <TabsContent value="analytics">
            <AnalyticsManagement token={token} />
          </TabsContent>

          {/* Tracking Management */}
          <TabsContent value="tracking">
            <TrackingManagement />
          </TabsContent>

          {/* Customer Management */}
          <TabsContent value="customers">
            <CustomerManagement />
          </TabsContent>

          {/* Products Management */}
          <TabsContent value="products">
            <ProductsManagement />
          </TabsContent>

          {/* Coupon Management */}
          <TabsContent value="coupons">
            <CouponManagement token={token} />
          </TabsContent>

          {/* Product Collections */}
          <TabsContent value="collections">
            <CollectionsManagement />
          </TabsContent>

          {/* Payment Gateways */}
          <TabsContent value="gateways">
            <GatewaySettings />
          </TabsContent>

          {/* Checkout Settings */}
          <TabsContent value="checkout">
            <CheckoutSettingsManagement />
          </TabsContent>

          {/* Paysafe Management */}
          <TabsContent value="paysafe">
            <PaysafeManagement />
          </TabsContent>

          {/* Email Logs */}
          <TabsContent value="emails">
            <EmailLogs />
          </TabsContent>

          {/* Email Templates */}
          <TabsContent value="templates">
            <EmailTemplates />
          </TabsContent>

          {/* System Settings */}
          <TabsContent value="settings">
            <SystemConfig />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
